/**
 * AssistantChat - Floating chat met CalqTool assistent
 * Context-gebonden: route, project, ingelogd. Thread persist in sessionStorage.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, ArrowRight, RotateCcw } from 'lucide-react';
import { api, getStoredAuthToken } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { playUiChime, playUiPop } from '../lib/uiSounds';
import { formatAssistantDisplayText } from '../lib/assistantPlainText';
import { ASSISTANT_OPEN_EVENT, type AssistantOpenDetail } from '../lib/assistantBus';

interface Message {
  role: string;
  content: string;
}

const STORAGE_KEY = 'calqtool_assistant_thread';
/** Sessiegebonden publieke thread (landing, geen login) */
const PUBLIC_STORAGE_KEY = 'calqtool_assistant_thread_public';
const CHAT_OPENED_ONCE_KEY = 'calqtool_assistant_hub_opened';

function seenAssistantStorageKey(tenantId: string) {
  return `calqtool_assistant_seen_assistant_${tenantId}`;
}

/** AssistantChat staat buiten Routes — projectId uit het pad halen. */
function projectIdFromPathname(pathname: string): string | undefined {
  if (!pathname.startsWith('/projects/')) return undefined;
  const seg = pathname.slice('/projects/'.length).split('/')[0];
  if (!seg || seg === 'new') return undefined;
  return seg;
}

/** Actieve dossier-tab voor assistent-context (gegevens | posten | resultaat | rapportage). */
function dossierFaseFromPathname(pathname: string): string | undefined {
  if (!projectIdFromPathname(pathname)) return undefined;
  if (pathname.includes('/gegevens')) return 'gegevens';
  if (pathname.includes('/posten')) return 'posten';
  if (pathname.includes('/resultaat') || pathname.includes('/resultaten')) return 'resultaat';
  if (pathname.includes('/rapportage')) return 'rapportage';
  return undefined;
}

function getWelcomeText(route: string, projectName?: string): string {
  const tabNav =
    'Tabs: Gegevens → Posten → Resultaat → Rapportage. Die blijven bij scrollen plakken; dezelfde stappen kun je in de zijkolom aanklikken.';
  const rapportage =
    ' Rapportage = calculatie (CSV, Excel, begroting-PDF) én offerte (preview, print); server-offerte-PDF en meer opties staan onder Exportinstellingen.';

  if (route === '/') {
    return 'Je bent ingelogd. Stel gerust een vraag over CalqTool of je dossiers — ook hier op de homepagina.';
  }
  if (projectIdFromPathname(route)) {
    const phaseHint =
      route.includes('/gegevens')
        ? ' Je zit nu bij stap Gegevens (context, documenten, tekeninganalyse).'
        : route.includes('/posten')
          ? ' Je zit nu bij stap Posten (prijsbibliotheek; na Regels opslaan worden Resultaat en Rapportage actief).'
          : route.includes('/resultaat') || route.includes('/resultaten')
            ? ' Je zit nu bij stap Resultaat (totalen en onderbouwing per regel).'
            : route.includes('/rapportage')
              ? ' Je zit nu bij stap Rapportage (export, offerte, scenario’s, feedback).'
              : '';
    const core = `${tabNav}${rapportage}`;
    return projectName
      ? `Je bent in dossier “${projectName}”.${phaseHint} ${core} Stel gerust een vraag.`
      : `Je bent in een dossier.${phaseHint} ${core} Stel gerust een vraag.`;
  }
  if (route === '/projects/new') {
    return 'Je start een nieuw dossier. Daarna opent je project met dezelfde vier stappen: Gegevens → Posten → Resultaat → Rapportage (tabs plakken bij scrollen; stappen in de zijkolom zijn klikbaar).';
  }
  if (route === '/onboarding') {
    return 'Je doorloopt de introductie. Die volgt hetzelfde ritme als een echt dossier: Gegevens → Posten → Resultaat → Rapportage.';
  }
  if (route === '/dashboard') {
    return 'Je bent op het dashboard. Open een dossier voor de volledige flow, of start met de Introductie in het menu.';
  }
  if (route === '/account' || route.startsWith('/account/')) {
    return 'Je bent op Account: profiel, beveiliging, Inloggen & apparaten (max. gelijktijdige sessies per pakket), en voor de accountbeheerder Organisatie en Facturatie. Stel gerust een vraag over rollen of inloggen.';
  }
  return 'Stel een vraag over bouwkosten, het prijsboek of hoe je door CalqTool navigeert. Bijv. “Verschil tussen calculatie-PDF en offerte?” of “Bereken badkamer renovatie 12 m²”.';
}

function getWelcomeTextPublic(): string {
  return 'Ik geef korte uitleg over CalqTool en de dossierflow — zonder account, zonder toegang tot jouw data. Voor prijsboek, echte berekeningen en export: log in of maak een account. Over accountbeheer (facturatie) en inloggen op meerdere apparaten: dat hangt van je pakket af — zie ook /prijzen.';
}

function getWelcomeTextPublicBlog(route: string): string {
  if (route === '/blog') {
    return 'Je leest de CalqTool-blog over bouwkosten en renovatie. Ik kan kort toelichten hoe artikelen aansluiten op het product; voor echte calculaties en dossiers heb je een account nodig.';
  }
  return 'Je leest een blogartikel. Ik kan het kort koppelen aan dossiers, export en pakketten — zonder toegang tot jouw data. Voor rekenen in de app: log in of start een account.';
}

function getSuggestedQuestions(route: string): string[] {
  if (route === '/') {
    return ['Wat is het prijsboek?', 'Hoe start ik na inloggen?'];
  }
  if (route === '/onboarding') {
    return ['Wat is een begrotingspost?', 'Waar vind ik export en offerte?'];
  }
  if (route === '/dashboard') {
    return ['Hoe start ik een dossier?', 'Waar zie ik mijn dossiers?'];
  }
  if (route === '/account' || route.startsWith('/account/')) {
    return ['Wat is een accountbeheerder?', 'Hoe werkt inloggen op meerdere apparaten?'];
  }
  if (route === '/projects/new') {
    return ['Wat gebeurt er na “dossier aanmaken”?', 'Waar zet ik klanttype?'];
  }
  if (projectIdFromPathname(route)) {
    if (route.includes('/rapportage')) {
      return [
        'Verschil tussen calculatie-PDF en offerte?',
        'Waar kan ik feedback over dit dossier sturen?',
      ];
    }
    if (route.includes('/resultaat')) {
      return ['Wat betekent materieel in de onderbouwing?', 'Wat is een bouwcluster?'];
    }
    if (route.includes('/posten')) {
      return ['Waarom zijn Resultaat en Rapportage dicht?', 'Hoe werkt bouwcluster?'];
    }
    if (route.includes('/gegevens')) {
      return [
        'Kan ik documenten uit de bibliotheek analyseren zonder opnieuw te uploaden?',
        'Waarom staat er indexjaar 2024 bij het prijspeil?',
      ];
    }
    return ['Waar vind ik export en offerte?', 'Hoe wissel ik snel van tab?'];
  }
  return ['Verschil calculatie en offerte?', 'Hoe kies ik een bouwdeel uit de library?'];
}

/** Vaste lijst — zelfde volgorde als suggestieknoppen (voor “nog niet geklikt”) */
const PUBLIC_SUGGESTED_QUESTIONS: readonly string[] = [
  'Wat doet CalqTool in het kort?',
  'Hoe werkt een dossier in vier stappen?',
  'Wat is een accountbeheerder en mag ik op meerdere apparaten inloggen?',
];

function getSuggestedQuestionsPublic(): string[] {
  return [...PUBLIC_SUGGESTED_QUESTIONS];
}

function getSuggestedQuestionsPublicBlog(route: string): string[] {
  if (route === '/blog') {
    return ['Wat lees ik op deze blog?', 'Hoe helpt CalqTool bij renovatiebegroting?'];
  }
  return ['Hoe sluit dit artikel aan op CalqTool?', 'Waar vind ik pakketten en prijzen?'];
}

type AssistantKind = 'hidden' | 'public' | 'authed';

function getErrorMessage(error: unknown, status?: number): string {
  const msg = error instanceof Error ? error.message : 'Er is iets misgegaan';
  if (msg.includes('service_unavailable') || msg.includes('Assistant not configured') || status === 503) {
    return 'De assistent is tijdelijk niet beschikbaar. Probeer het later opnieuw of neem contact met ons op.';
  }
  if (msg.includes('rate_limit') || msg.includes('Too many') || status === 429) {
    return 'Je hebt te veel berichten verstuurd. Wacht even, of maak een account voor de volledige assistent.';
  }
  return msg;
}

export function AssistantChat() {
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipChimeRef = useRef(true);
  const prevPublicModeRef = useRef<boolean | null>(null);

  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';
  const onPublicDemoRoute =
    location.pathname === '/' ||
    location.pathname === '/demo' ||
    location.pathname === '/blog' ||
    location.pathname.startsWith('/blog/');
  /** Alleen homepage: strengere assistant rate-limit (niet /demo voor ingelogde gebruikers). */
  const isLandingHome = location.pathname === '/';
  const hasToken = !!getStoredAuthToken();
  const projectId = projectIdFromPathname(location.pathname);
  const [projectDisplayName, setProjectDisplayName] = useState<string | undefined>();

  /** Publiek alleen op / voor bezoekers; ingelogd overal de volledige assistent */
  const assistantKind: AssistantKind = user?.authenticated
    ? 'authed'
    : onPublicDemoRoute && !(authLoading && hasToken)
      ? 'public'
      : 'hidden';
  const isPublicMode = assistantKind === 'public';
  const effectiveSeenKey = isPublicMode ? 'public' : tenantId;

  const storageKey = `${STORAGE_KEY}_${tenantId}`;

  useEffect(() => {
    if (!user?.authenticated || !projectId) {
      setProjectDisplayName(undefined);
      return;
    }
    let cancelled = false;
    api
      .getProject(projectId, tenantId)
      .then((p) => {
        if (!cancelled) {
          const n = p.metadata?.name?.trim();
          setProjectDisplayName(n || undefined);
        }
      })
      .catch(() => {
        if (!cancelled) setProjectDisplayName(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.authenticated, projectId, tenantId]);

  useEffect(() => {
    if (assistantKind === 'hidden') return;
    const handler = (ev: Event) => {
      const ce = ev as CustomEvent<AssistantOpenDetail>;
      const draft = ce.detail?.draft;
      if (typeof draft !== 'string' || !draft.trim()) return;
      playUiPop();
      setInput(draft.trim());
      setOpen(true);
      window.setTimeout(() => inputRef.current?.focus(), 400);
    };
    window.addEventListener(ASSISTANT_OPEN_EVENT, handler as EventListener);
    return () => window.removeEventListener(ASSISTANT_OPEN_EVENT, handler as EventListener);
  }, [assistantKind]);

  const assistantMsgCount = useMemo(() => messages.filter((m) => m.role === 'assistant').length, [messages]);

  const publicQuestionsUsed = useMemo(() => {
    if (!isPublicMode) return new Set<string>();
    const allowed = new Set(PUBLIC_SUGGESTED_QUESTIONS);
    const used = new Set<string>();
    for (const m of messages) {
      if (m.role === 'user') {
        const t = m.content.trim();
        if (allowed.has(t)) used.add(t);
      }
    }
    return used;
  }, [isPublicMode, messages]);

  const publicQuestionsRemaining = useMemo(
    () => PUBLIC_SUGGESTED_QUESTIONS.filter((q) => !publicQuestionsUsed.has(q)),
    [publicQuestionsUsed]
  );

  const markAssistantSeen = useCallback(() => {
    sessionStorage.setItem(seenAssistantStorageKey(effectiveSeenKey), String(assistantMsgCount));
  }, [effectiveSeenKey, assistantMsgCount]);

  const fabBadgeCount = useMemo(() => {
    if (open) return 0;
    const seen = parseInt(sessionStorage.getItem(seenAssistantStorageKey(effectiveSeenKey)) ?? '0', 10);
    const unread = Math.max(0, assistantMsgCount - seen);
    if (unread > 0) return Math.min(9, unread);
    if (!sessionStorage.getItem(CHAT_OPENED_ONCE_KEY)) return 1;
    return 0;
  }, [open, assistantMsgCount, effectiveSeenKey]);

  useEffect(() => {
    const prev = prevPublicModeRef.current;
    prevPublicModeRef.current = isPublicMode;
    if (prev === null || prev === isPublicMode) return;
    setThreadId(null);
    setMessages([]);
    setInitError(null);
    setError(null);
  }, [isPublicMode]);

  useEffect(() => {
    if (!open || threadId) return;

    if (isPublicMode) {
      const stored = sessionStorage.getItem(PUBLIC_STORAGE_KEY);
      if (stored) {
        api
          .getPublicAssistantThread(stored)
          .then((res) => {
            setThreadId(stored);
            if (res.messages && res.messages.length > 0) {
              skipChimeRef.current = true;
              setMessages(res.messages);
            }
          })
          .catch(() => {
            createNewPublicThread();
          });
      } else {
        createNewPublicThread();
      }
      return;
    }

    if (!user?.authenticated) return;

    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      api
        .getAssistantThread(stored, tenantId)
        .then((res) => {
          setThreadId(stored);
          if (res.messages && res.messages.length > 0) {
            skipChimeRef.current = true;
            setMessages(res.messages);
          }
        })
        .catch(() => {
          createNewThread();
        });
    } else {
      createNewThread();
    }
  }, [user?.authenticated, open, threadId, tenantId, storageKey, isLandingHome, isPublicMode]);

  function createNewThread() {
    setInitError(null);
    api
      .createAssistantThread({ tenant_id: tenantId, source: isLandingHome ? 'landing' : undefined })
      .then(({ thread_id }) => {
        setThreadId(thread_id);
        skipChimeRef.current = true;
        setMessages([]);
        sessionStorage.setItem(storageKey, thread_id);
      })
      .catch((e) => {
        const msg = getErrorMessage(e);
        setInitError(msg);
      });
  }

  function createNewPublicThread() {
    setInitError(null);
    api
      .createPublicAssistantThread()
      .then(({ thread_id }) => {
        setThreadId(thread_id);
        skipChimeRef.current = true;
        setMessages([]);
        sessionStorage.setItem(PUBLIC_STORAGE_KEY, thread_id);
      })
      .catch((e) => {
        const msg = getErrorMessage(e);
        setInitError(msg);
      });
  }

  function restartPublicChat() {
    setError(null);
    setInitError(null);
    sessionStorage.removeItem(PUBLIC_STORAGE_KEY);
    setThreadId(null);
    setMessages([]);
    skipChimeRef.current = true;
  }

  useEffect(() => {
    if (open && threadId && messages.length === 0 && !initError) {
      if (isPublicMode) {
        api
          .getPublicAssistantThread(threadId)
          .then((res) => {
            if (res.messages.length > 0) {
              skipChimeRef.current = true;
              setMessages(res.messages);
            }
          })
          .catch(() => {});
        return;
      }
      if (!user?.authenticated) return;
      api
        .getAssistantThread(threadId, tenantId)
        .then((res) => {
          if (res.messages.length > 0) {
            skipChimeRef.current = true;
            setMessages(res.messages);
          }
        })
        .catch(() => {});
    }
  }, [open, threadId, messages.length, tenantId, initError, isPublicMode, user?.authenticated]);

  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last?.role !== 'assistant') return;
    if (skipChimeRef.current) {
      skipChimeRef.current = false;
      return;
    }
    playUiChime();
  }, [messages]);

  useEffect(() => {
    if (open) {
      sessionStorage.setItem(CHAT_OPENED_ONCE_KEY, '1');
      markAssistantSeen();
    }
  }, [open, assistantMsgCount, markAssistantSeen]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /** Voorkom achtergrond-scroll op mobiel zolang overlay + kaart open zijn */
  useEffect(() => {
    if (!open) return;
    const mq = window.matchMedia('(max-width: 639px)');
    const apply = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : '';
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !threadId || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    setError(null);

    try {
      if (isPublicMode) {
        const { message } = await api.sendPublicAssistantMessage(threadId, {
          content: text,
          page: location.pathname,
        });
        skipChimeRef.current = false;
        setMessages((prev) => [...prev, { role: 'assistant', content: message }]);
      } else {
        const context = {
          route: location.pathname,
          project_id: projectId || undefined,
          project_name: projectDisplayName,
          dossier_fase: dossierFaseFromPathname(location.pathname),
        };
        const { message } = await api.sendAssistantMessage(threadId, {
          content: text,
          tenant_id: tenantId,
          source: isLandingHome ? 'landing' : undefined,
          context,
        });
        skipChimeRef.current = false;
        setMessages((prev) => [...prev, { role: 'assistant', content: message }]);
      }
    } catch (e) {
      const res = e as { response?: { status?: number } };
      const status = res?.response?.status;
      setError(getErrorMessage(e, status));
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const assistantCount = messages.filter((m) => m.role === 'assistant').length;
  const showCta = isPublicMode
    ? assistantCount >= 1 && assistantCount <= 4
    : assistantCount >= 2 && assistantCount <= 4;

  if (assistantKind === 'hidden') {
    return null;
  }

  return (
    <div data-assistant-chat>
      <button
        type="button"
        onClick={() => {
          playUiPop();
          setOpen(true);
        }}
        className={`fixed right-4 z-40 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-primary-alt max-sm:bottom-[calc(5.25rem+env(safe-area-inset-bottom,0px))] sm:bottom-6 sm:right-6 ${fabBadgeCount > 0 ? 'animate-chat-attn' : ''}`}
        aria-label={
          fabBadgeCount > 0 ? `Open assistent, ${fabBadgeCount} nieuw${fabBadgeCount > 1 ? 'e' : ''}` : 'Open assistent'
        }
      >
        <MessageCircle className="h-8 w-8" />
        {fabBadgeCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white shadow-md ring-2 ring-white">
            {fabBadgeCount > 8 ? '9+' : fabBadgeCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] sm:hidden"
            aria-label="Sluit assistent"
            onClick={() => {
              markAssistantSeen();
              setOpen(false);
            }}
          />
          <div
            className="animate-fade-in fixed z-50 flex w-[min(380px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-surface-card shadow-xl max-sm:left-1/2 max-sm:max-h-[min(420px,calc(100dvh-6.5rem))] max-sm:h-[min(420px,calc(100dvh-6.5rem))] max-sm:-translate-x-1/2 max-sm:bottom-[max(1rem,calc(0.75rem+env(safe-area-inset-bottom,0px)))] sm:inset-auto sm:bottom-24 sm:right-6 sm:left-auto sm:top-auto sm:h-[min(420px,calc(100dvh-5.5rem))] sm:max-h-[85vh] sm:translate-x-0"
            role="dialog"
            aria-modal="true"
            aria-labelledby="assistant-chat-title"
          >
          <div className="flex shrink-0 items-center justify-between border-b border-border bg-navy px-4 py-3 text-white sm:pt-3">
            <div className="flex min-w-0 items-center gap-2">
              <img src="/assets/assistant-chat.png" alt="" className="h-8 w-8 shrink-0 object-contain opacity-90" aria-hidden />
              <span id="assistant-chat-title" className="truncate font-semibold">
                {isPublicMode ? 'CalqTool — eerste hulp' : 'CalqTool Assistent'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                markAssistantSeen();
                setOpen(false);
              }}
              className="shrink-0 rounded p-2 hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Sluiten"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 pb-2 space-y-3 sm:p-4">
            {initError && (
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {initError}
                <p className="mt-2">
                  <Link to="/troubleshoot" className="font-medium text-primary hover:underline">
                    Problemen oplossen →
                  </Link>
                </p>
              </div>
            )}
            {!initError && messages.length === 0 && !loading && (
              <div className="space-y-2">
                <p className="text-sm text-text-muted">
                  {isPublicMode
                    ? location.pathname === '/blog' || location.pathname.startsWith('/blog/')
                      ? getWelcomeTextPublicBlog(location.pathname)
                      : getWelcomeTextPublic()
                    : getWelcomeText(location.pathname, projectDisplayName)}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(isPublicMode
                    ? location.pathname === '/blog' || location.pathname.startsWith('/blog/')
                      ? getSuggestedQuestionsPublicBlog(location.pathname)
                      : getSuggestedQuestionsPublic()
                    : getSuggestedQuestions(location.pathname)
                  ).map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setInput(q)}
                      className="rounded-full bg-primary/10 px-2.5 py-1 text-xs text-primary hover:bg-primary/20"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex w-full ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[min(100%,90%)] animate-fade-in rounded-lg px-3 py-2 text-sm break-words whitespace-pre-wrap sm:max-w-[min(100%,22rem)] ${
                    m.role === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-surface text-text'
                  }`}
                >
                  {m.role === 'assistant' ? formatAssistantDisplayText(m.content) : m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex w-full justify-start">
                <div className="max-w-[min(100%,90%)] rounded-lg bg-surface px-3 py-2 text-sm text-text-muted sm:max-w-[min(100%,22rem)]">
                  Bezig...
                </div>
              </div>
            )}
            {showCta && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
                <p className="mb-2 font-medium text-text">
                  {isPublicMode ? 'Wil je verder met echte dossiers en berekeningen?' : 'Klaar om te beginnen?'}
                </p>
                <Link
                  to="/login?returnTo=%2Fprojects%2Fnew"
                  className="inline-flex items-center gap-1 text-primary font-medium hover:underline"
                >
                  Account aanmaken / inloggen
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <div ref={scrollRef} />
          </div>

          {isPublicMode && !initError && messages.length > 0 && (
            <div className="shrink-0 border-t border-border bg-surface/90 px-3 py-2 sm:px-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-text">Nog een vraag?</p>
                <button
                  type="button"
                  onClick={() => {
                    playUiPop();
                    restartPublicChat();
                  }}
                  disabled={loading}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Nieuw gesprek
                </button>
              </div>
              {publicQuestionsRemaining.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {publicQuestionsRemaining.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => {
                        playUiPop();
                        setInput(q);
                      }}
                      disabled={loading || !threadId}
                      className="rounded-full bg-primary/10 px-2.5 py-1 text-left text-xs text-primary hover:bg-primary/20 disabled:opacity-50"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] leading-snug text-text-muted sm:text-xs">
                  Typ gerust zelf iets hieronder, of start een nieuw gesprek.
                </p>
              )}
            </div>
          )}

          <div className="shrink-0 border-t border-border p-3 pb-[max(0.75rem,calc(0.5rem+env(safe-area-inset-bottom,0px)))] sm:pb-3">
            <p className="mb-2 text-[10px] leading-snug text-text-muted sm:text-xs">
              Antwoorden zijn ter informatie en geen professioneel, juridisch of financieel advies. Controleer altijd
              kritieke keuzes met een vakman.{' '}
              <Link to="/terms" className="text-primary hover:underline">
                Voorwaarden
              </Link>
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Typ je vraag..."
                className="min-w-0 flex-1 rounded-lg border border-border px-3 py-2.5 text-base sm:text-sm focus:border-primary focus:outline-none"
                disabled={loading || !threadId || !!initError}
                enterKeyHint="send"
              />
              <button
                type="submit"
                disabled={loading || !threadId || !input.trim() || !!initError}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary text-white disabled:opacity-50 sm:h-10 sm:w-10"
                aria-label="Verstuur"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
